import styled from '@emotion/styled';
import type { ReactNode } from 'react';
import { cloneElement, isValidElement, useEffect, useId, useRef, useState } from 'react';
import { Icon } from '@components';

export interface CollapsibleDrawerProps {
  /** Contenido que se muestra/oculta al expandir/colapsar */
  readonly children: ReactNode;
  /**
   * Botón que abre/cierra el drawer. Debe ser un elemento <button> (por accesibilidad).
   * Si no se pasa, se usa el botón por defecto con defaultTriggerLabel.
   */
  readonly trigger?: React.ReactElement;
  /** Texto del botón por defecto cuando no se usa trigger personalizado */
  readonly defaultTriggerLabel?: string;
  /** aria-label del botón por defecto */
  readonly defaultTriggerAriaLabel?: string;
  /** Inicialmente abierto */
  readonly defaultOpen?: boolean;
  /** Callback al cambiar estado open/closed */
  readonly onToggle?: (open: boolean) => void;
  /** Identificador del panel para aria-controls en el botón */
  readonly id?: string;
  /** aria-label del panel (accesibilidad). Si no se pasa, se usa defaultTriggerLabel. */
  readonly panelAriaLabel?: string;
  /** Si true, al abrir el drawer hace scroll hasta el punto más bajo del contenido revelado */
  readonly autoScroll?: boolean;
  /** Dirección del scroll al abrir el drawer */
  readonly scrollDirection?: 'start' | 'end';
}

const DrawerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const DrawerPanel = styled.section<{ isOpen: boolean }>`
  display: grid;
  grid-template-rows: ${(props) => (props.isOpen ? '1fr' : '0fr')};
  transition: grid-template-rows 0.3s ease-out;
  min-height: 0;
`;

const DrawerInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

const DrawerContent = styled.div`
  padding: ${(props) => props.theme.spacing(2)} 0;
  color: ${(props) => props.theme.colors.text.mediumEmphasis};
  font-family: ${(props) => props.theme.typography.body4Regular.fontFamily};
  font-size: ${(props) => props.theme.typography.body4Regular.fontSize};
  line-height: ${(props) => props.theme.typography.body4Regular.lineHeight};
`;

const TriggerIconWrapper = styled.span<{ isOpen: boolean }>`
  display: inline-flex;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const DefaultTrigger = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(1)};
  padding: ${(props) => props.theme.spacing(1)} 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${(props) => props.theme.colors.accent.A[600]};
  font-family: ${(props) => props.theme.typography.body4Bold.fontFamily};
  font-size: ${(props) => props.theme.typography.body4Bold.fontSize};
  font-weight: ${(props) => props.theme.typography.body4Bold.fontWeight};
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }

  &:active {
    opacity: 0.7;
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.accent.A[500]};
    outline-offset: 2px;
  }
`;

const SCROLL_AFTER_OPEN_MS = 320;

export function CollapsibleDrawer({
  children,
  trigger,
  defaultTriggerLabel = 'Mostrar más',
  defaultTriggerAriaLabel,
  defaultOpen = false,
  onToggle,
  id: controlledId,
  panelAriaLabel,
  autoScroll = false,
  scrollDirection = 'start',
}: CollapsibleDrawerProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const generatedId = useId();
  const panelId = controlledId ?? `collapsible-drawer-${generatedId.replaceAll(':', '')}`;
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !autoScroll || !contentRef.current) return;
    const timer = globalThis.setTimeout(() => {
      contentRef.current?.scrollIntoView({ block: scrollDirection, behavior: 'smooth' });
    }, SCROLL_AFTER_OPEN_MS);
    return () => globalThis.clearTimeout(timer);
  }, [isOpen, autoScroll, scrollDirection]);

  const handleToggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    onToggle?.(next);
  };

  const defaultTriggerAriaLabelResolved =
    defaultTriggerAriaLabel ?? (isOpen ? 'Ocultar' : defaultTriggerLabel);

  const isCustomButton = trigger && isValidElement(trigger) && trigger.type === 'button';
  const triggerButton = isCustomButton ? (
    cloneElement(trigger as React.ReactElement<{ onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void }>, {
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        (trigger.props as { onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void }).onClick?.(e);
        handleToggle();
      },
      ...({
        'aria-expanded': isOpen,
        'aria-controls': panelId,
      } as Record<string, unknown>),
    })
  ) : (
    <DefaultTrigger
      type="button"
      onClick={handleToggle}
      aria-expanded={isOpen}
      aria-controls={panelId}
      aria-label={defaultTriggerAriaLabelResolved}
    >
      <TriggerIconWrapper isOpen={isOpen}>
        <Icon name="caret-down" size={20} />
      </TriggerIconWrapper>
      {defaultTriggerLabel}
    </DefaultTrigger>
  );

  return (
    <DrawerWrapper>
      {triggerButton}
      <DrawerPanel
        isOpen={isOpen}
        id={panelId}
        aria-label={panelAriaLabel ?? defaultTriggerLabel}
        aria-hidden={!isOpen}
      >
        <DrawerInner ref={contentRef}>
          <DrawerContent>{children}</DrawerContent>
        </DrawerInner>
      </DrawerPanel>
    </DrawerWrapper>
  );
}
